import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/getIsNav4Enabled.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

// eslint-disable-next-line import/no-cycle
import { scrollToElementInEditor } from './editorAnnotations.ts';

export const CREATE_COMMENT_SELECTION_CLASS = 'inline-create-comment-selection';
export const HIGHLIGHT_CLASS = 'inline-highlight';
export const ACTIVE_CLASS = 'active-highlight';

type CommentDataType = {
	commentIndex: number | undefined;
	totalComments: number | undefined;
};

type CommentElement = HTMLElement | null | undefined;

export type ScrollCommentIntoViewType = {
	commentElement: CommentElement;
	isFabricPage?: boolean;
	useInstantScroll?: boolean;
	isAbove?: boolean;
	commentThreadAnnotationId?: string;
	isCommentsPanel?: boolean;
};

export type ScrollCommentIntoViewTypeOld = {
	commentElement: CommentElement;
	isFabricPage?: boolean;
	useInstantScroll?: boolean;
	isAbove?: boolean;
	commentThreadContainerId?: string;
	isCommentsPanel?: boolean;
};

export type ScrollHighlightIntoViewInViewportType = {
	commentElement: CommentElement;
	scrollRootElement: HTMLElement | (Window & typeof globalThis);
	currentPageOffset: number;
	currentWindowHeight: number;
	isAbove: boolean;
	isFabricPage: boolean;
	useInstantScroll: boolean;
};

export const getCommentIndexAndCount = () => {
	const commentData: CommentDataType = {
		commentIndex: undefined,
		totalComments: undefined,
	};

	if (
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__ &&
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']
	) {
		commentData['commentIndex'] = Number(
			window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']['position'],
		);
		commentData['totalComments'] = Number(
			window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']['totalComments'],
		);
	}

	return commentData;
};

export const getDistinctMarks = (pageMode: 'view' | 'edit') => {
	const marks = Array.from(document.querySelectorAll(getInlineMarkSelector(pageMode))).map(
		(node) => {
			if (pageMode === 'edit') {
				return (node as HTMLElement).id;
			} else {
				return getMarkerRef(node as HTMLElement);
			}
		},
	);

	/* In case of overlapping comments there will be multiple tags with the same markerRefs. We need to remove
	duplicates in that case so we can navigate between comments correctly */
	return new Set(marks);
};

/* Gets Sorted MarkerRefs in DOM and filters ones returned by back-end API for Unresolved comments */
export const getSortedMarkerRefs = (
	markerRefList: string[],
	pageMode: 'view' | 'edit' = 'view',
) => {
	const distinctMarks = [...getDistinctMarks(pageMode)];

	return distinctMarks.filter((mark) => mark && markerRefList.includes(mark)) as string[];
};

// WS-2618 We have old macros that still render out tiny comments, so changing selector to look for both
export const getInlineMarkSelector = (pageMode = 'view') => {
	if (pageMode === 'edit') {
		return "[annotationtype='inlineComment']";
	} else {
		return '[data-mark-annotation-type="inlineComment"], .inline-comment-marker';
	}
};

export const clearActiveHighlight = () => {
	const activeHighlights = document.querySelectorAll(`.${ACTIVE_CLASS}`);

	activeHighlights.forEach((activeHighlight) => {
		activeHighlight.classList.remove(ACTIVE_CLASS);
	});
};

const getNextTextNodeOld = (node?: Node | null, boundingParentNode?: Node | null) => {
	let currentNode = node;
	let nextTextNode: Node | null | undefined = currentNode?.nextSibling;

	// If the node has no next sibling, grab it's parent's next sibling until we find a node
	while (!nextTextNode) {
		nextTextNode = currentNode?.parentElement?.nextSibling;

		if (nextTextNode) {
			// WS-2551 - If the bounding parent exists and we're out of bounds of it, we're done looking
			if (boundingParentNode && !boundingParentNode.contains(nextTextNode)) {
				nextTextNode = null;
				break;
			}

			// If we've landed on a text node, then break out
			if (nextTextNode.nodeType === Node.TEXT_NODE) {
				break;
			}

			// If the node exists and it's a hard break (<br />), we do one of two things:
			//   1. If it has a bounding parent, break out because the <br /> signifies the end of the selection
			//   2. If not, then move to the next sibling because we don't count hard breaks in selections
			if ((nextTextNode as HTMLElement)?.tagName === 'BR') {
				if (boundingParentNode) {
					break;
				} else {
					nextTextNode = nextTextNode.nextSibling;
				}
			}
		} else {
			// If the text node we tried to access is null, we need to jump up a generation
			currentNode = currentNode?.parentNode;
		}
	}

	// Search for the first text node in the next node
	while (nextTextNode && nextTextNode.nodeType !== Node.TEXT_NODE) {
		nextTextNode = nextTextNode.firstChild;
	}

	return nextTextNode;
};

export const getNextTextNode = (
	currentNode?: Node | null,
	boundingNode?: Node | null,
	isInlineMark?: boolean,
): Node | null | undefined => {
	let nextTextNode: Node | null | undefined = isInlineMark ? currentNode : currentNode?.nextSibling;

	while (nextTextNode && nextTextNode.nodeType !== Node.TEXT_NODE) {
		// If the text node is the bounding node, return that node
		if (boundingNode && nextTextNode?.isSameNode(boundingNode)) {
			return nextTextNode;
		}

		// Check if the node is a hard break (<br />) or an element with role="presentation" we do one of two things:
		if ((nextTextNode as HTMLElement)?.tagName === 'BR') {
			// If it has a bounding parent, break out because the <br /> signifies the end of the selection
			if (boundingNode) {
				return nextTextNode;
			}
			// If not, then move to the next sibling because we don't count hard breaks in selections
			nextTextNode = nextTextNode.nextSibling;
		} else if (
			(nextTextNode as HTMLElement)?.attributes?.getNamedItem('role')?.value === 'presentation'
		) {
			// If the element has role="presentation", move to the parent element's sibling because this current node does not contain a text node
			nextTextNode = nextTextNode.parentNode?.nextSibling;
		} else if (isEmojiNode(nextTextNode)) {
			// If we encounter an emoji and it's also the bounding node, return nothing
			if (boundingNode && nextTextNode?.isSameNode(boundingNode)) {
				return null;
			}

			// Otherwise, just return the node after it and keep going
			nextTextNode = nextTextNode.nextSibling;
		} else {
			// Otherwise, search for the first text node in the next node
			nextTextNode = nextTextNode.firstChild;
		}
	}

	// If we've found the text node, return that node
	if (nextTextNode?.nodeType === Node.TEXT_NODE) {
		return nextTextNode;
	}

	// If the text node we tried to access is null, we need to jump up a generation
	if (!nextTextNode) {
		return getNextTextNode(currentNode?.parentNode, boundingNode);
	}
};

const extractTextNodesIntoRangesOld = (selectedRange: Range) => {
	const rangesToHighlight: Range[] = [];
	const { startContainer, startOffset, endContainer, endOffset } = selectedRange;

	// NOTE: It's not possible for us to get here with anything other than "#text" nodes
	//       for the start container due to our highlighting restrictions, so we only
	//       need to check the type for endContainer when we get a selected range

	// WS-2551 - Triple clicks are the only thing that can cause the endContainer to be a non-text node
	const isTripleClick = endContainer.nodeType !== Node.TEXT_NODE;

	// If the start and end containers don't match, we need to loop through and extract
	// the text nodes out all common elements and create ranges to represent each of them
	if (!startContainer.isSameNode(endContainer)) {
		let boundingParentNode: Node | null | undefined;
		// Push the starting node into a range
		const startingRange = new Range();
		startingRange.setStart(startContainer, startOffset);
		startingRange.setEnd(startContainer, (startContainer as Text).length);
		rangesToHighlight.push(startingRange);

		// WS-2551 - In the case of a triple click, we only want to contain our selection to the
		// first non-comment parent wrapping the starting container to ensure we don't over select
		if (isTripleClick) {
			boundingParentNode = startContainer.parentNode;
			while (
				!boundingParentNode ||
				boundingParentNode.nodeType === Node.TEXT_NODE ||
				getMarkerRef(boundingParentNode as HTMLElement) !== null
			) {
				boundingParentNode = boundingParentNode?.parentNode;
			}
		} else {
			// Push the ending node into a range only if it's not a triple click
			const endingRange = new Range();

			endingRange.setStart(endContainer, 0);
			endingRange.setEnd(endContainer, endOffset);
			rangesToHighlight.push(endingRange);
		}

		// Iterate through all the inner nodes and extract the text ranges
		let node = getNextTextNodeOld(startContainer, boundingParentNode);
		while (node && !node.isSameNode(endContainer) && node.nodeType === Node.TEXT_NODE) {
			if ((node as Text).length) {
				const range = new Range();
				range.selectNodeContents(node);
				rangesToHighlight.push(range);
			}

			node = getNextTextNodeOld(node, boundingParentNode);
		}
	} else {
		// If they are the same container it's much simpler and we can just use the range
		rangesToHighlight.push(selectedRange);
	}

	return rangesToHighlight;
};

const isEmojiNode = (node?: Node) => {
	if (!node) {
		return false;
	}

	const attributes = (node as HTMLElement)?.attributes;
	const classList = (node as HTMLElement)?.classList;

	return (
		(attributes && attributes.getNamedItem('data-emoji-id')) ||
		(classList && classList.contains('emoji-common-node'))
	);
};

const extractTextNodesIntoRanges = (selectedRange: Range) => {
	const rangesToHighlight: Range[] = [];
	const { startContainer, startOffset, endContainer, endOffset } = selectedRange;

	// NOTE: It's not possible for us to get here with anything other than "#text" nodes
	//       for the start container due to our highlighting restrictions, so we only
	//       need to check the type for endContainer when we get a selected range

	// The endContainer is a non-text node when the user either:
	// 1. Triple clicks a node
	// 2. Highlights a header
	// 3. The highlight goes to the next node but does not actually highlight anything (offset === 0)
	const isNonTextNodeEndContainer = endContainer.nodeType !== Node.TEXT_NODE;

	// If highlight is just one node (i.e. same containers), it's much simpler and we can just use the range
	if (startContainer.isSameNode(endContainer)) {
		rangesToHighlight.push(selectedRange);
		return rangesToHighlight;
	}

	// If the start and end containers don't match, we need to loop through and extract
	// the text nodes out all common elements and create ranges to represent each of them
	let boundingNode: Node | null | undefined;
	// Push the starting node into a range
	const startingRange = new Range();
	startingRange.setStart(startContainer, startOffset);
	startingRange.setEnd(startContainer, (startContainer as Text).length);
	rangesToHighlight.push(startingRange);

	// In the case of a non-text node endContainer, we want to set a bounding node to the endContainer so we know
	// to stop traversing the DOM when hidding that bounding node
	if (isNonTextNodeEndContainer) {
		// If the end container is non-text and an emoji, we need to jump up a level or else it is
		// going to be bound to a node that we will never be able to check and thus never exit
		if (isEmojiNode(endContainer)) {
			boundingNode = endContainer.parentNode;
		} else {
			boundingNode = endContainer;
		}
	} else {
		// Push the ending node into a range
		const endingRange = new Range();
		endingRange.setStart(endContainer, 0);
		endingRange.setEnd(endContainer, endOffset);
		rangesToHighlight.push(endingRange);
	}

	// Get the first "inner" node between the start and end containers
	let node = getNextTextNode(startContainer, boundingNode);

	// Iterate through all the inner nodes and extract the text ranges
	while (node && !node.isSameNode(endContainer) && node.nodeType === Node.TEXT_NODE) {
		if ((node as Text).length) {
			const range = new Range();
			range.selectNodeContents(node);
			rangesToHighlight.push(range);
		}

		node = getNextTextNode(node, boundingNode);
	}

	return rangesToHighlight;
};

export const getHighlightOffset = (
	isFabricPage: boolean,
	isCreateDialogOpen: boolean = false,
	markerRef: string | undefined = '',
) => {
	const highlightClass = CREATE_COMMENT_SELECTION_CLASS;
	let markOffsetTop = 0;

	// Measure the offset of the highlight from the renderer wrapper
	// WS-2618 We have old macros that render tiny inline comments so we need to look for both fabric and tiny marker refs
	if (markerRef) {
		const highlight: HTMLElement | null = getHighlightFromMarkerRef(markerRef);

		markOffsetTop = calculateElementOffset(highlight);
	} else {
		const selection: Selection | null = window.getSelection();

		// If there is a user selection, we need to create a new temp "highlight" for this
		// new selection as it is removed when the temp "highlight" is created
		const isNewSelection = selection && selection.type == 'Range' && !isCreateDialogOpen;
		if (isNewSelection) {
			// Reset any existing DOM selections
			resetTemporaryDOMHighlight();

			// Mark the selection in the DOM temporarily
			createTemporaryDOMHighlight(false);
		}

		const createCommentHighlight: HTMLElement | null = document.querySelector(`.${highlightClass}`);

		markOffsetTop = calculateElementOffset(createCommentHighlight);
	}

	// On a fabric page, the highlight is positioned relative to the renderer
	// wrapper. To correctly position the sidebar we need to measure the offset
	// of the renderer wrapper and the offset of the highlight from the wrapper
	if (isFabricPage) {
		const rendererWrapper: HTMLElement | null = document.querySelector(
			'#content .ak-renderer-wrapper',
		);

		if (rendererWrapper) {
			markOffsetTop += rendererWrapper.offsetTop;
		}
	} else {
		// In tiny, we need to remove the title and author info offset to position it correctly
		const pageMetadata: HTMLElement | null = document.querySelector(
			'#content-body .page-metadata-modification-info',
		);

		if (pageMetadata) {
			markOffsetTop -= pageMetadata.offsetTop - pageMetadata.offsetHeight;
		}
	}

	return markOffsetTop;
};

export const createTemporaryDOMHighlight = (useNewHighlightLogic?: boolean) => {
	const highlightClass = CREATE_COMMENT_SELECTION_CLASS;
	const selection = window.getSelection();

	if (!selection || selection.type !== 'Range') {
		// Throw error?
		return;
	}

	// Extract the range
	const selectedRange = selection.getRangeAt(0);

	// Get an array of the ranges to make highlights
	const rangesToHighlight = useNewHighlightLogic
		? extractTextNodesIntoRanges(selectedRange)
		: extractTextNodesIntoRangesOld(selectedRange);

	rangesToHighlight.forEach((range) => {
		// Create a new wrapper element with the correct class
		const highlightWrapper = document.createElement('span');
		highlightWrapper.classList.add(highlightClass);
		// Extract the contents of the range and add it to the new wrapper
		highlightWrapper.appendChild(range.extractContents());

		// Re-insert the wrapper back into the range
		range.insertNode(highlightWrapper);
	});

	// Clear the selection
	selection.removeAllRanges();
};

const unwrapTextNodes = (wrappedNodes: Element[]) => {
	while (wrappedNodes.length) {
		const node = wrappedNodes.shift();

		if (!node) {
			return;
		}

		const { parentNode } = node;

		// Extract the text contents of the selection node out of the temp wrapper
		while (node.firstChild) {
			parentNode?.insertBefore(node.firstChild, node);
		}

		// After the text has been extracted, delete the wrapper
		parentNode?.removeChild(node);

		// Normalize the parent to fix any text node fragmentation
		parentNode?.normalize();
	}
};

export const resetTemporaryDOMHighlight = () => {
	const highlightClass = CREATE_COMMENT_SELECTION_CLASS;
	// Grab all the newly created selection nodes
	const tempSelectionNodes = Array.from(document.querySelectorAll(`.${highlightClass}`));

	unwrapTextNodes(tempSelectionNodes);
};

export const convertDOMHighlightToMark = (
	isFabricPage: boolean,
	markerRef: string | null,
	isActive = true,
) => {
	const createCommentSelectionNodes = document.querySelectorAll(
		`.${CREATE_COMMENT_SELECTION_CLASS}`,
	);

	const ref = markerRef || '';

	// If we find selection nodes in the DOM, we need to add some attributes to them,
	// based on the page type, so they behave correctly when the user clicks a highlight
	if (createCommentSelectionNodes.length) {
		createCommentSelectionNodes.forEach((selectionNode) => {
			if (isFabricPage) {
				selectionNode.setAttribute(FABRIC_PAGE_MARKER_REF_ATTRIBUTE, ref);
				selectionNode.setAttribute('data-mark-annotation-type', 'inlineComment');
			} else {
				selectionNode.setAttribute(TINY_PAGE_MARKER_REF_ATTRIBUTE, ref);
				selectionNode.classList.add('inline-comment-marker');
			}

			const classesToAdd = [HIGHLIGHT_CLASS];

			if (isActive) {
				classesToAdd.push(ACTIVE_CLASS);
			}

			// Add the classes needed
			selectionNode.classList.add(...classesToAdd);

			// Finally, remove the selection class to make it a full mark
			selectionNode.classList.remove(CREATE_COMMENT_SELECTION_CLASS);
		});
	}
};

const setHighlightsAsActive = (highlightElement: Element, skipClassCheck = false) => {
	if (skipClassCheck || highlightElement.classList.contains(HIGHLIGHT_CLASS)) {
		highlightElement.classList.add(ACTIVE_CLASS);
	}

	// We need to set all the children of this highlight to active as well
	const { children } = highlightElement;
	for (let i = 0; i < children.length; ++i) {
		setHighlightsAsActive(children[i], skipClassCheck);
	}
};

/* We cannot just set the active state on the element that was clicked due to the
 * fact that a highlight can span multiple elements and even other comments in the
 * DOM depending on when they are added. For example, we can have a DOM structure that
 * looks like this:
 *
 *    <p>
 *      Some text
 *      <span data-id="marker-ref-1" class="inline-highlight">
 *        An important
 *        <span data-id="marker-ref-2" class="inline-highlight">
 *          passage that was highlighted
 *        </span>
 *        by the user
 *      </span>
 *      with complicated higlights
 *    </p>
 *    <p>
 *      <span data-id="marker-ref-3" class="inline-highlight">
 *        <span data-id="marker-ref-1" class="inline-highlight">
 *          Text in the next
 *        </span>
 *        paragraph that is also highlighted
 *      </span>
 *      And unselected
 *    </p>
 *
 * Lets say the user tries to select the comment with markerRef "marker-ref-1" that spans
 * text across both paragraphs which should result in the text "An important passage that
 * was highlighted by the user with complicated highlightsText in the next" being activated.
 * We need to activate all the nodes of that we can find with the matching markerRef, but
 * then ALSO all of the nodes that are children of those parents which contain other highlights.
 * The user will see gaps if we do not properly add the active class to ALL of these nodes.
 */
export const setCommentAsActive = (markerRef: string | null, skipClassCheck = false) => {
	const highlightMarks = getAllHighlightsFromMarkerRef(markerRef) || [];

	highlightMarks.forEach((highlight) => setHighlightsAsActive(highlight, skipClassCheck));
};

export const isHighlightActive = (markerRef: string | null) => {
	const highlight = getHighlightFromMarkerRef(markerRef || '');

	return Boolean(highlight && highlight.classList.contains(ACTIVE_CLASS));
};

export const removeMarkInDOM = (markerRef: string | null) => {
	const highlightMarksToRemove = Array.from(getAllHighlightsFromMarkerRef(markerRef));

	unwrapTextNodes(highlightMarksToRemove);
	clearActiveHighlight();
};

export const getUnresolvedAnnotations = (): Set<string> => {
	const highlightedAnnotations = Array.from(
		document.querySelectorAll('.ak-editor-annotation-blur, .ak-editor-annotation-focus'),
	);
	const highlightedAnnotationIds = highlightedAnnotations
		.map((annotation) => annotation?.closest('.annotationView-content-wrap')?.id)
		.filter((id): id is string => Boolean(id));

	return new Set(highlightedAnnotationIds);
};

type ADF = {
	type: string;
	content: any[];
	version: number;
} | null;

export const FABRIC_PAGE_MARKER_REF_ATTRIBUTE = 'data-id';
export const TINY_PAGE_MARKER_REF_ATTRIBUTE = 'data-ref';

const hasAdfContent = (input: ADF) => {
	const content = input && input.content;
	return content && content.some(validateContent);
};

// Check to make sure if we have item.type as paragraph content is not empty (user did not type anything)
// or user has put in blank spaces(empty string) and clicked save
// Any type other than paragraph is valid (e.g:- when user inserts divider, status etc without content)
const validateContent = (item: any) => item.type !== 'paragraph' || validateParagraph(item);

const validateParagraph = (item: any) => {
	if (item.content) {
		return item.content.some(isParagraphNotEmpty);
	}

	return false;
};

const isParagraphNotEmpty = (item: any) => item.type !== 'text' || item.text.trim() !== '';

export const isEmptyAdf = (adf: ADF) => !adf || !hasAdfContent(adf);

export const getMarkerRefAttribute = (isFabricPage: any) =>
	isFabricPage ? FABRIC_PAGE_MARKER_REF_ATTRIBUTE : TINY_PAGE_MARKER_REF_ATTRIBUTE;

// WS-2618 We have old macros that still render out tiny comments, so we need to check for both to get the marker ref
export const getMarkerRef = (element: HTMLElement | null) => {
	let markerRef: string | null = null;
	if (element) {
		markerRef = element.getAttribute(FABRIC_PAGE_MARKER_REF_ATTRIBUTE);
		if (!markerRef) {
			markerRef = element.getAttribute(TINY_PAGE_MARKER_REF_ATTRIBUTE);
		}
	}

	return markerRef;
};

// WS-2618 We have old macros that still render out tiny comments, so we need to check for both marker refs to get a comment
export const getHighlightFromMarkerRef: (
	markerRef?: string,
	additionalQueryScope?: string,
) => HTMLElement | null = (markerRef, additionalQueryScope = '') => {
	if (!markerRef) {
		return null;
	}

	// We need to allow for additional query scope to be added to specify particular markerRef comments
	return document.querySelector(
		`${additionalQueryScope} [${FABRIC_PAGE_MARKER_REF_ATTRIBUTE}="${markerRef}"], ${additionalQueryScope} [${TINY_PAGE_MARKER_REF_ATTRIBUTE}="${markerRef}"]`,
	);
};

// WS-2618 We have old macros that still render out tiny comments, so we need to check for both marker refs to get a comment
export const getAllHighlightsFromMarkerRef = (markerRef: any) => {
	return document.querySelectorAll(
		`[${FABRIC_PAGE_MARKER_REF_ATTRIBUTE}="${markerRef}"], [${TINY_PAGE_MARKER_REF_ATTRIBUTE}="${markerRef}"]`,
	);
};

// List of keys to allow propagation in the comment editor
export const keyPropagationList = ['enter', 'escape'];

const getDimensions = () => {
	try {
		return {
			currentWindowHeight: window.innerHeight || (window.top?.innerHeight ?? 0),
			currentPageOffset: window.scrollY || (window.top?.scrollY ?? 0),
		};
	} catch {
		// Catch cross origin iframe error https://hello.atlassian.net/browse/CBT-907
		return { currentWindowHeight: 0, currentPageOffset: 0 };
	}
};

const getDimensionsForNav4 = () => {
	try {
		const nav4ScrollParent = document.getElementById('AkMainContent');
		const currentPageOffset = !!nav4ScrollParent
			? nav4ScrollParent.scrollTop
			: window.scrollY || (window.top?.scrollY ?? 0);

		return {
			currentWindowHeight: window.innerHeight || (window.top?.innerHeight ?? 0),
			currentPageOffset,
		};
	} catch {
		// Catch cross origin iframe error https://hello.atlassian.net/browse/CBT-907
		return { currentWindowHeight: 0, currentPageOffset: 0 };
	}
};

const COMMENT_CONTAINER_OFFSET = 350;

// @TODO PGEXP-351 follow-up: We probably need to change this behavior for Nav4
export const scrollPageCommentIntoView = (
	commentElement: HTMLElement | null,
	useInstantScroll = false,
	isAbove = false,
) => {
	if (!commentElement) {
		return;
	}

	const { currentWindowHeight, currentPageOffset } = getDimensions();

	const offset =
		calculateElementOffset(commentElement) +
		commentElement.offsetHeight +
		(isAbove ? -COMMENT_CONTAINER_OFFSET : 0);

	// If the position of the comment box is more than halfway down the screen, reposition it to the center
	if (offset <= currentPageOffset || offset > currentPageOffset + currentWindowHeight * (2 / 3)) {
		const windowOffset = currentWindowHeight / 5;
		window.scrollTo({
			top: offset - windowOffset,
			behavior: useInstantScroll ? ('instant' as ScrollBehavior) : 'smooth', // focused comments use an instant scroll
		});
	}
};

// scrolls to the element
export const handleScrollToElement = ({
	commentId,
	targetCommentId,
	isEditor,
	viewingRoomOffset = -150,
	useInstantScroll = false,
}: {
	commentId: string;
	targetCommentId: string;
	isEditor: boolean;
	viewingRoomOffset?: number;
	useInstantScroll?: boolean;
}) => {
	if (commentId !== targetCommentId) {
		return;
	}

	const commentElement = document.querySelector(`[data-comment-id="${commentId}"]`) as HTMLElement;
	if (isEditor && commentElement) {
		scrollToElementInEditor({ targetElement: commentElement, viewingRoomOffset });
	} else {
		scrollToElementWithViewingRoom({
			element: commentElement,
			viewingRoomOffset,
			useInstantScroll,
		});
	}
};

export const scrollHighlightIntoViewInViewport = ({
	commentElement,
	scrollRootElement,
	currentPageOffset,
	currentWindowHeight,
	isAbove,
	isFabricPage,
	useInstantScroll,
}: ScrollHighlightIntoViewInViewportType) => {
	if (!commentElement) {
		return;
	}
	// We may be in an iframe so use window.top
	const offset =
		calculateElementOffset(commentElement) +
		commentElement.offsetHeight +
		(isAbove ? -COMMENT_CONTAINER_OFFSET : 0);
	const rendererOffset =
		(document.querySelector('#content .ak-renderer-wrapper') as HTMLElement)?.offsetTop || 0;

	// If the position of the highlight is more than halfway down the screen, reposition it to the center
	if (
		offset + rendererOffset <= currentPageOffset ||
		offset + rendererOffset > currentPageOffset + currentWindowHeight * (2 / 3)
	) {
		const windowOffset = isFabricPage ? currentWindowHeight / 5 : currentWindowHeight / 3;

		scrollRootElement.scrollTo({
			top: offset - windowOffset,
			behavior: useInstantScroll ? ('instant' as ScrollBehavior) : 'smooth', // focused comments use an instant scroll
		});
	}
};

// Function to scroll a comment into view with the correct offsets
export const scrollCommentIntoViewOld = ({
	commentElement,
	isFabricPage,
	useInstantScroll = false,
	isAbove = false,
	commentThreadContainerId,
	isCommentsPanel,
}: ScrollCommentIntoViewTypeOld) => {
	if (!commentElement) {
		return;
	}

	const isNav4Enabled = getIsNav4Enabled();
	const nodeToConsider = getNodeForTableORMacro(commentElement) || commentElement;
	const targetCommentHighlight = nodeToConsider.getBoundingClientRect();
	const { scrollRootElement, getDimensions } = getScrollRootContext(isNav4Enabled);
	const { currentWindowHeight, currentPageOffset } = getDimensions();
	const behavior = useInstantScroll ? ('instant' as ScrollBehavior) : 'smooth';

	if (isCommentsPanel) {
		const element = document.querySelector(
			`[data-testid='${commentThreadContainerId}']`,
		) as HTMLElement;

		const commentThreadParentElement = element.getBoundingClientRect().top;

		const commentThreadSelector = document.querySelector(
			`[data-testid='comments-panel-list-container']`,
		);

		const inlineCommentPosition = element?.getBoundingClientRect();

		const documentContent = document.getElementById('content');
		const documentContentTopPosition = documentContent?.getBoundingClientRect().top;

		const commentAboveViewport =
			inlineCommentPosition &&
			documentContentTopPosition &&
			Math.floor(inlineCommentPosition.top) < Math.floor(documentContentTopPosition);

		const commentBelowViewport =
			inlineCommentPosition && inlineCommentPosition.top + 200 > window.innerHeight;

		const commentHighlightAboveViewPort =
			Math.floor(targetCommentHighlight.bottom) < currentPageOffset;
		const commentHighlightBelowViewPort = targetCommentHighlight.top + 200 > window.innerHeight;

		/**
		 * Determine which panel or section needs to be scrolled:
		 * the content area or the comments panel.
		 * We need to find out if the highlight or panel is above or below the viewport.
		 * This will help us decide which one to scroll.
		 */
		if (commentHighlightAboveViewPort || commentHighlightBelowViewPort) {
			// if the comment highlight is above or below viewport use content area to scroll;
			scrollRootElement.scrollBy({
				top:
					window.innerHeight -
					commentThreadParentElement -
					(window.innerHeight - targetCommentHighlight.top),
				behavior,
			});
		} else if (
			commentThreadSelector &&
			(commentAboveViewport ||
				commentBelowViewport ||
				window.innerHeight -
					commentThreadParentElement -
					(window.innerHeight - targetCommentHighlight.top) <
					0)
		) {
			// if the comment thread is above or below viewport or
			// there is difference in top of comment thread and highlight if completely visible in the viewport
			//  use commentThreadSelector as an element to scroll ;
			commentThreadSelector.scrollBy({
				top:
					window.innerHeight -
					targetCommentHighlight.top -
					(window.innerHeight - commentThreadParentElement),
				behavior,
			});
		} else {
			scrollRootElement.scrollBy({
				top: targetCommentHighlight.top - commentThreadParentElement,
				behavior,
			});
		}
	} else {
		// We may be in an iframe so use window.top
		const offset =
			calculateElementOffset(commentElement) +
			commentElement.offsetHeight +
			(isAbove ? -COMMENT_CONTAINER_OFFSET : 0);
		const rendererOffset =
			(document.querySelector('#content .ak-renderer-wrapper') as HTMLElement)?.offsetTop || 0;

		// If the position of the highlight is more than halfway down the screen, reposition it to the center
		if (
			offset + rendererOffset <= currentPageOffset ||
			offset + rendererOffset > currentPageOffset + currentWindowHeight * (2 / 3)
		) {
			const windowOffset = isFabricPage ? currentWindowHeight / 5 : currentWindowHeight / 3;

			scrollRootElement.scrollTo({
				top: offset - windowOffset,
				behavior: useInstantScroll ? ('instant' as ScrollBehavior) : 'smooth', // focused comments use an instant scroll
			});
		}

		//If comment is in expand, open respective expand
	}
	checkElementExpand(commentElement, isFabricPage);
};

// Function to scroll a comment into view with the correct offsets
export const scrollCommentIntoView = ({
	commentElement,
	isFabricPage,
	useInstantScroll = false,
	isAbove = false,
	commentThreadAnnotationId,
	isCommentsPanel,
}: ScrollCommentIntoViewType) => {
	if (!fg('confluence_frontend_content_wrapper')) {
		const commentThreadContainerId = `comment-thread-${commentThreadAnnotationId}-container`;
		scrollCommentIntoViewOld({
			commentElement,
			isFabricPage,
			useInstantScroll,
			isAbove,
			commentThreadContainerId,
			isCommentsPanel,
		});
		return;
	}

	if (!commentElement) {
		return;
	}

	const isNav4Enabled = getIsNav4Enabled();
	const nodeToConsider = getNodeForTableORMacro(commentElement) || commentElement;
	const targetCommentHighlight = nodeToConsider.getBoundingClientRect();
	const { scrollRootElement, getDimensions: getDimensionsFromScrollRootContext } =
		getScrollRootContext(isNav4Enabled);
	const { currentWindowHeight, currentPageOffset } = getDimensionsFromScrollRootContext();
	if (isCommentsPanel) {
		const commentHighlightAboveViewPort =
			Math.floor(targetCommentHighlight.bottom) < currentPageOffset;
		const commentHighlightBelowViewPort = targetCommentHighlight.top + 200 > window.innerHeight;

		// Scroll the highlight into view in the main content body
		if (commentHighlightAboveViewPort || commentHighlightBelowViewPort) {
			scrollHighlightIntoViewInViewport({
				commentElement,
				scrollRootElement,
				currentPageOffset,
				currentWindowHeight,
				isAbove,
				isFabricPage: !!isFabricPage,
				useInstantScroll,
			});
		}

		} else {
		scrollHighlightIntoViewInViewport({
			commentElement,
			scrollRootElement,
			currentPageOffset,
			currentWindowHeight,
			isAbove,
			isFabricPage: !!isFabricPage,
			useInstantScroll,
		});
	}

	//If comment is in expand, open respective expand
	checkElementExpand(commentElement, isFabricPage);
};

/**
 * Scroll an element into view if element is out of visible area.
 * Visible area is defined as scroll container with 60px threshold at top and bottom.
 * This implementation is mimicking how prosemirror scrolls elements into view.
 */
export const scrollTargetIntoViewIfNeeded = (target: Element, scrollContainer: Element) => {
	const rect = target.getBoundingClientRect();
	const parentRect = scrollContainer.getBoundingClientRect();
	let moveY = 0;

	// The distance (in pixels) between the target and the end of the visible viewport at which point,
	// when scrolling the target into view, scrolling takes place.
	const SCROLL_THRESHOLD = 60;
	// The extra space (in pixels) that is left above or below the target when it is scrolled into view.
	const SCROLL_MARGIN = 120;

	// Note: visible area of the container refers to parentRect.top/bottom +/- SCROLL_THRESHOLD
	if (rect.top < parentRect.top + SCROLL_THRESHOLD) {
		// If the target is above the top of the visible area of the container, moveY should be negative to bring target down into view
		moveY = -(parentRect.top - rect.top + SCROLL_MARGIN);
	} else if (rect.bottom > parentRect.bottom - SCROLL_THRESHOLD) {
		// If the target's bottom is below the bottom of the visible area of the container, move Y should be positive to bring target up into view
		moveY = rect.bottom - parentRect.bottom + SCROLL_MARGIN;
	}

	if (moveY) {
		scrollContainer.scrollTop += moveY;
	}
};

// I understand the appeal to dynamically generate things like this, but when strings are used as IDs or keys,
// it's more important to be explicit about those keys than smart in code. If there's a bug with this component,
// the id is the thing people will search for; and if it's dynamically generated, it's impossible to find.
export const getSidebarId = (isEditor?: boolean, isViewCommentMode?: boolean) => {
	if (isEditor && isViewCommentMode) {
		return 'editor-comments-sidebar';
	} else if (isEditor && !isViewCommentMode) {
		return 'editor-create-sidebar';
	} else if (!isEditor && isViewCommentMode) {
		return 'renderer-comments-sidebar';
	} else {
		return 'renderer-create-sidebar';
	}
};

// Navigate between comments using the correct offsets
export const scrollToComment = (
	commentElement: HTMLElement | null,
	previousCommentElement: CommentElement,
	isFabricPage: any,
	isEditor?: boolean,
) => {
	if (!commentElement) return;

	const isNav4Enabled = getIsNav4Enabled();
	const { scrollRootElement, scrollRootOffset } = getScrollRootContext(isNav4Enabled);

	// if current open comment is out of viewport, we need to scroll so that the next comment is in viewport
	const sidebarId = getSidebarId(isEditor, true); // Hard-coding isViewCommentMode to true because navigation only exists in the view scenario
	const inlineCommentSidebar = document.getElementById(sidebarId);
	const inlineCommentPosition = inlineCommentSidebar?.getBoundingClientRect();

	const documentContent = document.getElementById('content');
	const documentContentTopPosition = documentContent?.getBoundingClientRect().top;

	const commentAboveViewport =
		inlineCommentPosition &&
		documentContentTopPosition &&
		inlineCommentPosition.top < documentContentTopPosition + scrollRootOffset;

	const commentBelowViewport =
		inlineCommentPosition && inlineCommentPosition.top + 200 > window.innerHeight;

	if (commentAboveViewport || commentBelowViewport) {
		scrollCommentIntoView({ commentElement, isFabricPage });
		return;
	}

	const previousOffset = previousCommentElement
		? calculateElementOffset(previousCommentElement)
		: 0;

	const offset = calculateElementOffset(commentElement);

	const differenceOffset = offset - previousOffset;
	scrollRootElement.scrollBy({
		top: differenceOffset,
		behavior: 'smooth', // navigation uses a smooth scroll
	});

	// If comment is in expand, open respective expand
	checkElementExpand(commentElement, isFabricPage);
};

const adjustNodeForStickyHeader = (stickyHeaderElement: HTMLElement | null) => {
	let adjustedElement = stickyHeaderElement;

	const markerRef = getMarkerRef(adjustedElement);

	// If this is exists it's a commited comment, look for its counterpart in the real table
	// TODO: Support new highlights when we allow it
	if (markerRef) {
		// WS-2618 We have old macros that render tiny inline comments so we need to look for both fabric and tiny marker refs
		adjustedElement = getHighlightFromMarkerRef(markerRef, '.pm-table-wrapper');
	}

	return adjustedElement;
};

export const getNodeForTableORMacro = (element: HTMLElement | null | undefined) => {
	let nodeToReturn = element;

	// Sticky table headers duplicate the elements in the DOM so we need to check if this is the real one or not
	// If the highlight has a parent with the class .pm-table-sticky-wrapper it is the fake one
	if (nodeToReturn && nodeToReturn.closest && nodeToReturn.closest('.pm-table-sticky-wrapper')) {
		nodeToReturn = adjustNodeForStickyHeader(nodeToReturn);
	}

	if (nodeToReturn) {
		// WS-2718 - If a comment is in an extension macro
		const extensionParent = nodeToReturn.closest('.ak-renderer-extension') as HTMLElement | null;

		if (extensionParent) {
			nodeToReturn = extensionParent;
		}
	}
	return nodeToReturn;
};

// Function to calculate a comment's offset taking nested elements into consideration
// relativeToElement allows the caller to optionally specify an element
// in `element`'s offsetParents to stop the calculation early at.
// This allows an easy way to calculate offset relative to a certain container, for example, the Editor.
export const calculateElementOffset = (
	element: HTMLElement | null | undefined,
	relativeToElement?: HTMLElement | null | undefined,
	isRendererAnnotationProviderEnabled?: boolean,
) => {
	let elementOffsetTop = 0;
	let nodeToMeasure = element;

	// Sticky table headers duplicate the elements in the DOM so we need to check if this is the real one or not
	// If the highlight has a parent with the class .pm-table-sticky-wrapper it is the fake one
	if (nodeToMeasure && nodeToMeasure.closest && nodeToMeasure.closest('.pm-table-sticky-wrapper')) {
		// The RAP annotation draft mark is applied to the both pm-table-sticky-wrapper as well as the pm-table-wrapper.
		// Need to check only pm-table-wrapper annotation draft mark for the RAP enabled
		nodeToMeasure = isRendererAnnotationProviderEnabled
			? (document.querySelector(
					`.pm-table-wrapper [data-annotation-draft-mark="true"]`,
				) as HTMLElement | null)
			: adjustNodeForStickyHeader(nodeToMeasure);
	}

	// Iterate through the offset parents until we are back to the ak-renderer-wrapper to get the
	// proper offset Fabric Pages wrap the content in a .ak-renderer-wrapper element, but Tiny
	// pages do not have anything like that, but will return null when we get to <body>
	while (
		nodeToMeasure &&
		!nodeToMeasure.classList.contains('ak-renderer-wrapper') &&
		nodeToMeasure !== relativeToElement
	) {
		elementOffsetTop += nodeToMeasure.offsetTop;
		nodeToMeasure = nodeToMeasure.offsetParent as HTMLElement | null;
	}

	// WS-2718 - If a comment is in an extension macro, we need to add its container offset as well
	if (nodeToMeasure) {
		const extensionParent = nodeToMeasure.closest('.ak-renderer-extension') as HTMLElement | null;

		if (extensionParent) {
			elementOffsetTop += extensionParent.offsetTop;
		}
	}

	return elementOffsetTop;
};

//Helper function to determine if passed in element has a child expand button
//Returns the button element if true, otherwise null
export const hasExpandButton = (element: HTMLElement, isFabricPage?: boolean) => {
	//get the child of element passed in
	const expandElement = element?.hasChildNodes() ? (element?.firstChild as HTMLElement) : element;

	//check if element is expand button and return element if so
	if (isFabricPage) {
		if (expandElement?.tagName?.toLowerCase() === 'button') {
			return expandElement;
		}
	} else if (expandElement?.classList?.contains('expand-control')) {
		return expandElement?.firstChild as HTMLElement; //it's one child down for tiny pages
	}
	return null;
};

// Common function to handle multi-level expand for both editor and renderer pages
export const checkElementExpandMultiLevelCommon = (
	element: HTMLElement,
	options: {
		isEditor?: boolean;
		isFabricPage?: boolean;
		expandSelectors: string[];
		getExpandButton: (node: HTMLElement) => HTMLElement | null;
		isExpanded: (button: HTMLElement) => boolean;
		onExpand?: (button: HTMLElement) => void;
	},
) => {
	const { expandSelectors, getExpandButton, isExpanded, onExpand } = options;
	const nodeToCheck = element;

	// Check if comment is in any expand
	const isInExpand = expandSelectors.some((selector) => nodeToCheck?.closest?.(selector));

	if (!isInExpand) {
		return;
	}

	// Find all expand buttons that need to be opened, starting from the innermost
	const expandButtonsToOpen: HTMLElement[] = [];
	let currentNode: HTMLElement | null = nodeToCheck;

	while (currentNode) {
		// Check if current node has an expand button
		const expandButton = getExpandButton(currentNode);

		if (expandButton) {
			// Check if this expand is already opened
			if (!isExpanded(expandButton)) {
				expandButtonsToOpen.push(expandButton);
			}
		}

		currentNode = currentNode?.parentElement;
	}

	// Open all expand buttons from innermost to outermost
	const buttonsToClick = [...expandButtonsToOpen].reverse();
	buttonsToClick.forEach((expandButton) => {
		if (onExpand) {
			onExpand(expandButton);
		}
		expandButton.click();
	});
};

// Function to determine if comment is within expand, and open respective expand with support for multiple levels
export const checkElementExpandMultiLevel = (element: HTMLElement, isFabricPage?: boolean) => {
	const expandElementClassname = isFabricPage ? '.expand-content-wrapper' : '.expand-content';
	const nestedExpandElementClassname = '.nestedExpand-content-wrapper';
	const expandSelectors = [expandElementClassname, nestedExpandElementClassname];

	const getExpandButton = (node: HTMLElement) => hasExpandButton(node, isFabricPage);

	const isExpanded = (button: HTMLElement) => {
		return isFabricPage
			? button.getAttribute('aria-expanded') === 'true'
			: button.className.includes('expanded');
	};

	checkElementExpandMultiLevelCommon(element, {
		isFabricPage,
		expandSelectors,
		getExpandButton,
		isExpanded,
	});
};

// Function to determine if comment is within expand, and open respective expand
export const checkElementExpand = (element: HTMLElement, isFabricPage?: boolean) => {
	checkElementExpandMultiLevel(element, isFabricPage);
};

export const getNextMarkerRef = (
	action: 'next' | 'previous',
	index: number | undefined,
	markerRefList: string[],
): string | void => {
	if (markerRefList.length === 0 || index === undefined || index === -1) {
		return;
	}
	let commentIndex = -1;
	//Navigate to first inline comment if we're on the last comment and the user clicks next,
	// or to the last comment if we're on the first comment and the user clicks previous
	if (action === 'next') {
		commentIndex = index === markerRefList.length - 1 ? 0 : index + 1;
	} else if (action === 'previous') {
		commentIndex = index === 0 ? markerRefList.length - 1 : index - 1;
	}

	return markerRefList[commentIndex];
};

export const getNextUnreadThreadKey = (
	index: number | undefined,
	markerRefList: string[],
	unreadMarkerRefList?: string[],
) => {
	if (
		index === undefined ||
		markerRefList.length === 0 ||
		index === -1 ||
		!unreadMarkerRefList ||
		unreadMarkerRefList.length === 0
	) {
		return;
	}
	let commentIndex = !fg('confluence_frontend_unread_comments_fixes')
		? index
		: index % markerRefList.length; // Handle case where index is beyond array length
	const refListLength = markerRefList.length;
	let iterations = 0;
	while (
		markerRefList[commentIndex] !== undefined &&
		!unreadMarkerRefList.includes(markerRefList[commentIndex]) &&
		iterations < refListLength
	) {
		commentIndex = (commentIndex + 1) % markerRefList.length;
		iterations++;
	}
	return markerRefList[commentIndex];
};

/** Helper functions for clipboard api */

export const isClipboardApiSupported = (): boolean =>
	navigator.clipboard && typeof navigator.clipboard.writeText === 'function';

/** In the IE, window contain clipboard api instead of navigator. */
export const isIEClipboardApiSupported = (): boolean =>
	(window as any).clipboardData && typeof (window as any).clipboardData.setData === 'function';

export const copyToClipboardIE = (textToCopy: string): Promise<void> =>
	new Promise<void>((resolve: () => void, reject: (str: string) => void) => {
		if (isIEClipboardApiSupported()) {
			(window as any).clipboardData.setData('text', textToCopy);
			resolve();
		} else {
			reject('IE clipboard api is not supported');
		}
	});

export const copyToClipboard = async (text: string): Promise<void> => {
	if (isClipboardApiSupported()) {
		await navigator.clipboard.writeText(text);
	}
};

export const legacyCopyToClipboard = (text: string, ref: HTMLDivElement | null): Promise<void> =>
	new Promise<void>((res, rej) => {
		if (!ref) {
			rej();
			return;
		}

		const textarea = document.createElement('textarea');
		textarea.readOnly = true;
		textarea.defaultValue = text;
		ref.appendChild(textarea);
		textarea.select();
		const wasCopied = document.execCommand('copy');
		ref.removeChild(textarea);

		wasCopied ? res() : rej();
	});

// for both editor and live pages
export const calculateEditorOffsetFromParent = (
	annotationElement: HTMLElement | null | undefined,
	scrollParentSelector: string,
) => {
	const scrollParent = document.querySelector(scrollParentSelector) as HTMLElement;
	return calculateElementOffset(annotationElement, scrollParent);
};

export const calculateRendererOffsetFromParent = (
	annotationElement: HTMLElement | null | undefined,
	scrollParentSelector: string,
	isView: boolean,
) => {
	let offset = 0;
	if (annotationElement) {
		// If the annotation is the wrapperDOM, we actually need #content to be the scrollParent
		const scrollParent = document.querySelector(
			'div#content[data-inline-comments-target="true"]',
		) as HTMLElement;

		if (scrollParent) {
			let annotationOffset: number;

			if (isView) {
				annotationOffset = calculateElementOffset(annotationElement);
			} else {
				// If the annotation is the wrapperDOM, we need to find the actual highlight instead
				const actualHiglightElement: HTMLElement | null =
					scrollParent.querySelector(scrollParentSelector);
				annotationOffset = calculateElementOffset(actualHiglightElement, undefined, true);
			}
			offset = annotationOffset + scrollParent.offsetTop;
		}
	}
	return offset;
};

/**
 *
 * @param entries
 * @returns top OR undefined
 */
export const getCommentPositionTopOfSectionEditor = (
	entries: ResizeObserverEntry[],
): number | undefined => {
	const windowHeight = window.innerHeight;

	if (
		expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true) ||
		fg('confluence_frontend_unify_footer_comment_space_ld')
	) {
		if (!fg('confluence_frontend_unify_footer_comment_space_ld')) {
			// When fg is ON, we would have verified that there's no
			// endOfPageRecommendations before adding this ResizeObserver.
			// So no need to check it here.
			const endOfPageRecommendations = document.querySelector('.end-of-page-rec-wrapper');
			if (endOfPageRecommendations) {
				return 0;
			}
		}

		const editorTextArea = document.getElementById('ak-editor-textarea');
		const editorBottom = editorTextArea?.getBoundingClientRect().bottom;

		if (editorBottom && windowHeight) {
			// The minimum spacing between bottom of editor content and top of footer is 120px
			const minimumSpacing = 120;

			// - There's an existing fixed 24px margin below editor content.
			// - ObjectAddCommentContainer.tsx has a 64px height.
			// - We lift footer comment top by 14px to make it visually distinct
			//   when a short LiveDoc has some vs zero existing footer comment.
			const editorBottomMargin = 24;
			const addFooterCommentHeight = 64;
			const extraFooterCommentHeightToShow = 14;

			const editorScrollParent = document.querySelector('.fabric-editor-popup-scroll-parent');
			const currentScroll = editorScrollParent?.scrollTop ?? 0;

			const editorBottomUnscrolled = editorBottom + currentScroll;
			const verticalDistanceFromWindowBottomUnscrolled = windowHeight - editorBottomUnscrolled;
			return Math.max(
				verticalDistanceFromWindowBottomUnscrolled -
					editorBottomMargin -
					addFooterCommentHeight -
					extraFooterCommentHeightToShow,
				minimumSpacing,
			);
		}
	} else {
		// The calculation is
		// 15px of marginTop on the comment container
		// 2px full-page-editor-style bottom which above the viewport
		// 1px border which is cumulates to 120px
		const thresholdTop = 102;

		const editorEntry = entries.find((entry) => entry.target.id === 'ak-editor-textarea');
		const editorBottom = editorEntry?.target.getBoundingClientRect().bottom;

		const editorContentHeight = document
			.querySelector(`.ak-editor-content-area .ProseMirror`)
			?.getBoundingClientRect().height;
		const documentContentBottom = document
			.querySelector(`[data-testid='full-page-editor-style']`)
			?.getBoundingClientRect().bottom;

		if (editorContentHeight) {
			// If the document content is larger than the window height, just set it to a static 120 to keep the proper spacing
			if (editorContentHeight > windowHeight) {
				return 120;
			}

			if (documentContentBottom && editorBottom) {
				const verticalDistance = documentContentBottom - editorBottom;
				if (verticalDistance >= thresholdTop && editorContentHeight <= windowHeight) {
					return verticalDistance - thresholdTop;
				} else if (verticalDistance <= thresholdTop) {
					return thresholdTop;
				}
			}
		}
	}
};

export const getCommentPositionTopOfSectionRenderer = (): number => {
	// This is deprecated, and will be replaced by a new approach that works in SSR.
	let newTop = 0;

	const endOfPageRecommendations = document.querySelector('.end-of-page-rec-wrapper');
	const legacyLabels = document.querySelector('[data-vc="view-page-labels-container"]');
	// no extra spacing if the page shows end-of-page recommendations or legacy labels
	if (!endOfPageRecommendations && !legacyLabels) {
		const rendererContent = document.getElementById('main-content');
		const rendererContentBottom = rendererContent?.getBoundingClientRect()?.bottom ?? 0;

		if (rendererContentBottom) {
			// - There's an existing fixed 80px margin below Page content (See
			//   next/packages/view-page-common/src/ViewPageContent.tsx).
			// - ObjectAddCommentContainer.tsx has a 64px height.
			// - We lift footer comment top by 14px (matching Editor version ^)
			//   to make it visually distinct when a short Page has some vs zero
			//   existing footer comment.
			const contentBottomMargin = 80;
			const addFooterCommentHeight = 64;
			const extraFooterCommentHeightToShow = 14;

			// Find the current scroll offset on the Page.
			// This is similar to implementation in getDimensionsForNav4(),
			// but when the window is very narrow, nav4ScrollParent.scrollTop
			// can be 0 (nav4ScrollParent element itself exists on page) while
			// window.scrollY is positive.
			// So this is a more robust implementation. Will explore whether
			// getDimensionsForNav4() can be updated to use this implementation
			// in a separate PR.
			const nav4ScrollParent = document.getElementById('AkMainContent');
			const currentPageOffset =
				(nav4ScrollParent?.scrollTop ?? 0) + (window.scrollY ?? window.top?.scrollY ?? 0);

			const rendererContentBottomUnscrolled = rendererContentBottom + currentPageOffset;
			const windowHeight = window.innerHeight;
			const verticalDistanceFromWindowBottomUnscrolled =
				windowHeight - rendererContentBottomUnscrolled - contentBottomMargin;

			newTop = Math.max(
				verticalDistanceFromWindowBottomUnscrolled -
					addFooterCommentHeight -
					extraFooterCommentHeightToShow,
				0,
			);
		}
	}
	return newTop;
};

export const scrollToElementWithViewingRoom = ({
	element,
	viewingRoomOffset = -150,
	useInstantScroll = false,
}: {
	element: HTMLElement;
	viewingRoomOffset?: number;
	useInstantScroll?: boolean;
}) => {
	if (!element) return;

	const isNav4Enabled = getIsNav4Enabled();
	const { useNav4ScrollRoot, scrollRootElement, scrollRootOffset } =
		getScrollRootContext(isNav4Enabled);

	const elementRect = element.getBoundingClientRect();
	const elementTopViewport = elementRect.top;

	const scrollRootTopViewport = useNav4ScrollRoot
		? (scrollRootElement as HTMLElement).getBoundingClientRect().top
		: 0;
	const elementTopFromScrollRoot = elementTopViewport - scrollRootTopViewport;

	const targetScrollPosition = elementTopFromScrollRoot + scrollRootOffset + viewingRoomOffset;
	scrollRootElement.scrollTo({
		top: targetScrollPosition,
		behavior: useInstantScroll ? ('instant' as ScrollBehavior) : 'smooth', // focused comments use an instant scroll
	});

	// open expand if the element is inside an expand
	checkElementExpand(element, true);
};

/**
 * Watches for a specific comment container to appear in the DOM and scrolls to it when found
 * Only active when confluence-fe-comments-deep-link-behavior feature gate is enabled
 */
export const watchForCommentContainerAndScroll = (commentId: string): (() => void) => {
	let isDisconnected = false;
	let commentFound = false;

	// Find the most specific container to observe
	const commentsPanelListContainer = document.querySelector(
		'[data-testid="comments-panel-list-container"]',
	);
	const targetContainer = commentsPanelListContainer || document.body;

	const observer = new MutationObserver(() => {
		if (isDisconnected || commentFound) return;

		// Look for the comment element directly
		const commentElement = targetContainer.querySelector(`[data-comment-id="${commentId}"]`);

		if (commentElement) {
			commentFound = true;
			commentElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'center',
			});
			isDisconnected = true;
			observer.disconnect();
		}
	});

	// Start observing only the specific container
	observer.observe(targetContainer, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['data-comment-id'],
	});

	// Return cleanup function
	return () => {
		if (!isDisconnected) {
			observer.disconnect();
			isDisconnected = true;
		}
	};
};

/**
 * Utility function to watch for comment rendering and scroll when all replies are visible
 * Uses MutationObserver to monitor DOM changes and button state changes
 */
export const watchForCommentRepliesAndScroll = ({
	targetCommentId,
	onScrollReady,
	onFallback,
	timeoutMs = 10000,
	markElement,
	containerId,
}: {
	targetCommentId: string;
	onScrollReady: (commentElement: Element) => void;
	onFallback?: () => void;
	timeoutMs?: number;
	markElement?: HTMLElement | null;
	containerId?: string;
}): (() => void) => {
	if (expValEquals('confluence_comments_use_new_observer', 'isEnabled', true)) {
		return watchForCommentRepliesAndScroll_V2({
			targetCommentId,
			onScrollReady,
			onFallback,
			timeoutMs,
			markElement,
			containerId,
		});
	}

	let commentFound = false;
	let isDisconnected = false;

	const observer = new MutationObserver((mutations) => {
		if (isDisconnected) return;

		// First, look for the comment element if we haven't found it yet
		if (!commentFound) {
			for (const mutation of mutations) {
				if (mutation.type === 'childList') {
					for (const addedNode of Array.from(mutation.addedNodes)) {
						if (addedNode.nodeType === Node.ELEMENT_NODE) {
							const element = addedNode as Element;

							// Check if this element or any of its descendants has the specific data-comment-id
							const containerElement = containerId ? document.getElementById(containerId) : null;
							const commentElement =
								containerElement ||
								element.querySelector?.(`[data-comment-id="${targetCommentId}"]`) ||
								(element.matches?.(`[data-comment-id="${targetCommentId}"]`) ? element : null);
							if (commentElement) {
								commentFound = true;
								// Early return if the comment is in the container element
								if (containerElement) {
									onScrollReady(containerElement);
									isDisconnected = true;
									observer.disconnect();
									return;
								}
								markElement && checkElementExpand(markElement, true);

								// Check the initial state of the show more button
								const showMoreButton = document.querySelector(
									'[data-testid="show-hidden-replies"]',
								);
								if (!showMoreButton) {
									onScrollReady(commentElement);
									isDisconnected = true;
									observer.disconnect();
									return;
								} else {
									// Trigger click on the show more button to expand all replies
									(showMoreButton as HTMLElement).click();
								}
								break;
							}
						}
					}
				}
			}
		}

		// If comment is found, watch for the show more button to disappear
		if (commentFound) {
			const commentElement = document.querySelector(`[data-comment-id="${targetCommentId}"]`);
			const showMoreButton = document.querySelector('[data-testid="show-hidden-replies"]');

			if (commentElement && !showMoreButton) {
				markElement && checkElementExpand(markElement, true);
				onScrollReady(commentElement);
				isDisconnected = true;
				observer.disconnect();
				return;
			}
		}
	});

	// Start observing
	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Clean up observer after specified timeout to prevent memory leaks
	const timeoutId = setTimeout(() => {
		if (!isDisconnected) {
			observer.disconnect();
			isDisconnected = true;

			// Fallback behavior if comment was found but button never disappeared
			if (commentFound) {
				const commentElement = document.querySelector(`[data-comment-id="${targetCommentId}"]`);
				if (commentElement) {
					// Check and open expand sections if needed before scrolling
					markElement && checkElementExpand(markElement, true);
					onScrollReady(commentElement);
				}
			} else if (onFallback) {
				onFallback();
			}
		}
	}, timeoutMs);

	// Return cleanup function
	return () => {
		if (!isDisconnected) {
			observer.disconnect();
			isDisconnected = true;
			clearTimeout(timeoutId);
		}
	};
};

/**
 * Find the comment element by ID or data-comment-id attribute
 */
const findCommentElement = (
	targetCommentId: string,
	containerId?: string,
	isFooterCommentInPanel?: boolean,
): Element | null => {
	// If isFooterCommentInPanel is true, use comment-thread-${targetCommentId}-container
	if (isFooterCommentInPanel) {
		const panelContainer = document.getElementById(`comment-thread-${targetCommentId}-container`);
		if (panelContainer) {
			return panelContainer;
		}
	}

	// Try container element first if containerId is provided
	if (containerId) {
		const container = document.getElementById(containerId);
		if (container) {
			return container;
		}
	}

	// Fall back to finding by data-comment-id
	return document.querySelector(`[data-comment-id="${targetCommentId}"]`);
};

/**
 * Find the show more button for the current comment element
 * The button can be inside the commentElement, in a sibling, or in the replies container
 */
export const findShowMoreButton = (
	commentElement: Element | null,
	targetCommentId: string,
): HTMLElement | null => {
	if (!commentElement) {
		return null;
	}

	// 1. Check within commentElement (descendants)
	const buttonInComment = commentElement.querySelector('[data-testid="show-hidden-replies"]');
	if (buttonInComment) {
		return buttonInComment as HTMLElement;
	}

	// 2. Check in parent element (siblings)
	const parentElement = commentElement.parentElement;
	const buttonInParent = parentElement?.querySelector('[data-testid="show-hidden-replies"]');
	if (buttonInParent) {
		return buttonInParent as HTMLElement;
	}

	// 3. Check in replies container (specific to this comment thread)
	const repliesContainer = document.querySelector(
		`[data-testid="comment-thread-${targetCommentId}-replies"]`,
	);
	const buttonInReplies = repliesContainer?.querySelector('[data-testid="show-hidden-replies"]');
	if (buttonInReplies) {
		return buttonInReplies as HTMLElement;
	}

	return null;
};

/**
 * Handle the comment element being found
 * Checks for show more button and either scrolls immediately or clicks to expand
 */
const handleCommentFound = (
	commentElement: Element,
	targetCommentId: string,
	containerId: string | undefined,
	markElement: HTMLElement | null | undefined,
	onScrollReady: (commentElement: Element) => void,
): boolean => {
	// If container element exists and we're using it, scroll immediately
	if (containerId && commentElement.id === containerId) {
		markElement && checkElementExpand(markElement, true);
		onScrollReady(commentElement);
		return true; // Indicates we should disconnect
	}

	// Expand mark element if needed
	markElement && checkElementExpand(markElement, true);

	// Check for show more button
	const showMoreButton = findShowMoreButton(commentElement, targetCommentId);

	if (!showMoreButton) {
		// No show more button, scroll immediately
		onScrollReady(commentElement);
		return true; // Indicates we should disconnect
	} else {
		// Show more button exists, click it to expand replies
		showMoreButton.click();
		return false; // Keep watching for button to disappear
	}
};

/**
 * Check if comment element exists in newly added nodes
 */
const checkAddedNodes = (
	addedNodes: NodeList,
	targetCommentId: string,
	containerId: string | undefined,
	isFooterCommentInPanel: boolean | undefined,
	onCommentFound: (commentElement: Element) => boolean,
): boolean => {
	for (const addedNode of Array.from(addedNodes)) {
		if (addedNode.nodeType !== Node.ELEMENT_NODE) {
			continue;
		}

		const element = addedNode as Element;

		// Check if this element or its descendants contains the comment
		let commentElement: Element | null = null;

		// First check for container element
		if (containerId) {
			const container = document.getElementById(containerId);
			if (container) {
				commentElement = container;
			}
		}

		// If not found, check for comment by data-comment-id
		if (!commentElement) {
			commentElement =
				element.querySelector?.(`[data-comment-id="${targetCommentId}"]`) ||
				(element.matches?.(`[data-comment-id="${targetCommentId}"]`) ? element : null);
		}

		// If isFooterCommentInPanel, also check for panel container
		if (!commentElement && isFooterCommentInPanel) {
			const panelContainer = document.getElementById(`comment-thread-${targetCommentId}-container`);
			if (panelContainer) {
				commentElement = panelContainer;
			}
		}

		if (commentElement) {
			const shouldDisconnect = onCommentFound(commentElement);
			if (shouldDisconnect) {
				return true; // Indicates we should stop processing
			}
		}
	}
	return false;
};

/**
 * Improved version of watchForCommentRepliesAndScroll with cleaner logic and better structure
 * Handles finding comment elements, container elements, and show more buttons more efficiently
 */
export const watchForCommentRepliesAndScroll_V2 = ({
	targetCommentId,
	onScrollReady,
	onFallback,
	timeoutMs = 10000,
	markElement,
	containerId,
	isFooterCommentInPanel = false,
}: {
	targetCommentId: string;
	onScrollReady: (commentElement: Element) => void;
	onFallback?: () => void;
	timeoutMs?: number;
	markElement?: HTMLElement | null;
	containerId?: string;
	isFooterCommentInPanel?: boolean;
}): (() => void) => {
	let commentFound = false;
	let isDisconnected = false;

	const handleCommentFoundWrapper = (commentElement: Element): boolean => {
		commentFound = true;
		return handleCommentFound(
			commentElement,
			targetCommentId,
			containerId,
			markElement,
			onScrollReady,
		);
	};

	const observer = new MutationObserver((mutations) => {
		if (isDisconnected) {
			return;
		}

		// First, look for the comment element if we haven't found it yet
		if (!commentFound) {
			for (const mutation of mutations) {
				if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
					const shouldStop = checkAddedNodes(
						mutation.addedNodes,
						targetCommentId,
						containerId,
						isFooterCommentInPanel,
						handleCommentFoundWrapper,
					);
					if (shouldStop) {
						isDisconnected = true;
						observer.disconnect();
						return;
					}
				}
			}
		}

		// If comment is found, watch for the show more button to disappear
		if (commentFound) {
			const commentElement = findCommentElement(
				targetCommentId,
				containerId,
				isFooterCommentInPanel,
			);
			const showMoreButton = findShowMoreButton(commentElement, targetCommentId);

			// If comment exists and show more button is gone, we're ready to scroll
			if (commentElement && !showMoreButton) {
				markElement && checkElementExpand(markElement, true);
				onScrollReady(commentElement);
				isDisconnected = true;
				observer.disconnect();
				return;
			}
		}
	});

	// Start observing
	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Clean up observer after specified timeout to prevent memory leaks
	const timeoutId = setTimeout(() => {
		if (!isDisconnected) {
			observer.disconnect();
			isDisconnected = true;

			// Fallback behavior if comment was found but button never disappeared
			if (commentFound) {
				const commentElement = findCommentElement(
					targetCommentId,
					containerId,
					isFooterCommentInPanel,
				);
				if (commentElement) {
					// Check and open expand sections if needed before scrolling
					markElement && checkElementExpand(markElement, true);
					onScrollReady(commentElement);
				}
			} else if (onFallback) {
				onFallback();
			}
		}
	}, timeoutMs);

	// Return cleanup function
	return () => {
		if (!isDisconnected) {
			observer.disconnect();
			isDisconnected = true;
			clearTimeout(timeoutId);
		}
	};
};

/**
 * Returns the HTMLElement or window that should be used as the scroll root. By default, this is the window.
 * For Nav 4, this is the div with id=AkMainContent, if the id exists in the DOM
 *
 * @param isNav4Enabled - boolean
 */
function getScrollRootContext(isNav4Enabled: boolean = false) {
	const nav4ScrollRootElement = document.getElementById('AkMainContent');
	const useNav4ScrollRoot = isNav4Enabled && !!nav4ScrollRootElement;

	const scrollRootElement = useNav4ScrollRoot ? nav4ScrollRootElement : window;

	const scrollRootOffset = useNav4ScrollRoot ? nav4ScrollRootElement.scrollTop : window.scrollY;

	return {
		useNav4ScrollRoot,
		scrollRootElement,
		scrollRootOffset,
		getDimensions: isNav4Enabled ? getDimensionsForNav4 : getDimensions,
	};
}

// Constants for comment thread visibility
const SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS = 8;
const NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW = 60;

const getTotalOffsetAboveCommentsPanelThreads = (generalCommentsHeaderHeight: number): number => {
	let objectSidebarPanelHeader = document.querySelector(
		"[data-testid='object-sidebar-panel-header']",
	);
	let additionalOffset = 0;
	if (!objectSidebarPanelHeader) {
		objectSidebarPanelHeader = document.querySelector(
			"[data-testid='scrolled-object-sidebar-panel-header']",
		);
		additionalOffset = SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS;
	}

	const objectSidebarPanelHeaderHeight =
		objectSidebarPanelHeader?.getBoundingClientRect().height ?? 0;
	const objectHeaderHeight =
		document.querySelector("[data-testid='object-header-container']")?.getBoundingClientRect()
			.height ?? 0;
	const topNavContainerHeight =
		document.querySelector("[data-testid='grid-topNav']")?.getBoundingClientRect().height ?? 0;
	const bannerContainerHeight =
		document.querySelector("[data-testid='banner-container']")?.getBoundingClientRect().height ?? 0;
	const editorControlsOnboardingBannerHeight =
		document
			.querySelector("[data-testid='editor-controls-onboarding-banner']")
			?.getBoundingClientRect().height ?? 0;
	const announcementBannerHeight =
		document.querySelector("[id='AkBanner']")?.getBoundingClientRect().height ?? 0;

	return (
		objectSidebarPanelHeaderHeight +
		objectHeaderHeight +
		topNavContainerHeight +
		bannerContainerHeight +
		additionalOffset +
		generalCommentsHeaderHeight +
		editorControlsOnboardingBannerHeight +
		announcementBannerHeight
	);
};

/**
 * Determines if a comment thread should be scrolled to the top based on its visibility
 */
export const shouldScrollCommentThreadToTop = (
	commentThreadElement: Element,
	commentType?: string,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();
	const generalCommentsHeaderHeight =
		commentType === 'general'
			? (document
					.querySelector("[data-testid='comment-section-separator']")
					?.getBoundingClientRect().height ?? 0)
			: 0;
	const totalPaddingAboveCommentThreadView = getTotalOffsetAboveCommentsPanelThreads(
		generalCommentsHeaderHeight,
	);

	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;
	const viewportTop = totalPaddingAboveCommentThreadView;
	const viewportBottom = window.innerHeight - objectSidebarPanelFooterHeight;
	const commentTopInView = commentThreadRect.top >= viewportTop;
	const commentBottomInView = commentThreadRect.bottom <= viewportBottom;
	if (commentTopInView && commentBottomInView) {
		// Comment is 100% in view
		return false;
	} else if (!commentTopInView && commentBottomInView) {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	} else if (commentTopInView && !commentBottomInView) {
		// Comment is <100% in view and the hidden part is the bottom of the comment
		const hiddenBottomHeight = commentThreadRect.bottom - viewportBottom;
		if (hiddenBottomHeight <= NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW) {
			// The hidden part is <= 60 pixels
			return false;
		} else {
			// The hidden part is > 60 pixels
			return true;
		}
	} else {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	}
};

/**
 * Determines if a comment thread reply is out of view
 */
export const isCommentThreadReplyOutOfView = (
	commentThreadElement: Element,
	commentType?: string,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();

	const generalCommentsHeaderHeight =
		commentType === 'general'
			? (document
					.querySelector("[data-testid='comment-section-separator']")
					?.getBoundingClientRect().height ?? 0)
			: 0;

	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;

	return (
		// Element is partially out of view below the comments panel viewport
		commentThreadRect.bottom + generalCommentsHeaderHeight >
		window.innerHeight - objectSidebarPanelFooterHeight
	);
};

/**
 * Returns the width and max-width for the comment threads
 */
export const getWidthAndMaxWidth = ({
	pageMode,
	pageWidthType,
	hasEndOfPageRecommendations,
}: {
	pageMode: PageMode;
	pageWidthType?: string | ContentAppearanceType;
	hasEndOfPageRecommendations: boolean;
}): { width: string; maxWidth: string } => {
	const endOfPageRecommendationsMaxWidth = '1000px';

	if (
		pageMode === PageMode.VIEW &&
		(fg('confluence_frontend_unify_footer_comment_width') ||
			expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true))
	) {
		// pageWidthType is a string like '"default"' / '"full-width"' in renderer mode, and is undefined in live doc mode
		const isDefaultWidth =
			pageWidthType && JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT;

		if (hasEndOfPageRecommendations) {
			// when end-of-page recommendations is present, always match its width (see EndOfPageRecContainer.tsx)
			return {
				width: isDefaultWidth ? '87.5%' : '100%',
				maxWidth: endOfPageRecommendationsMaxWidth,
			};
		} else {
			return {
				width: '100%',
				maxWidth: isDefaultWidth ? '760px' : '1800px', // if not defaultWidth, use full page width
			};
		}
	}

	if (
		pageMode !== PageMode.VIEW &&
		(fg('confluence_frontend_unify_footer_comment_width_ld') ||
			expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true))
	) {
		return {
			width: '100%',
			maxWidth:
				hasEndOfPageRecommendations &&
				expValEquals('confluence_ai_related_content_for_live_pages', 'isEnabled', true)
					? endOfPageRecommendationsMaxWidth
					: '100%',
		};
	}

	// Base case: when the fg for the current pageMode isn't ON, i.e. either of:
	// - pageMode === PageMode.VIEW && !fg('confluence_frontend_unify_footer_comment_width') && !expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true)
	// - pageMode !== PageMode.VIEW && !fg('confluence_frontend_unify_footer_comment_width_ld') && !expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true)
	// This block can be removed entirely once both fgs are rolled out.
	let maxWidth = '100%';
	if (pageWidthType) {
		maxWidth = JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT ? '1000px' : 'unset';
	}
	return { width: '100%', maxWidth };
};
